import {
  APICurrentUser,
  PersonalSetting,
  TraversalPathMatchingType,
} from '@ardoq/api-types';
import { TraversalBuilderState } from '../types';
import { applyStartSetExpansion } from './applyStartSetExpansion';
import { pathsToGraph } from './pathsToGraph';
import {
  filterOptions,
  tripleOptionToFilteredAndSortedTripleOptions,
} from './filterOptions';
import {
  setInstanceCounts,
  updateStartNodeCountForCollapsedHierarchy,
  invalidateInstanceCounts,
  setOptionCountLoadingState,
  setOptionsInstanceCounts,
  toggleShowOnlyOptionsWithInstanceCounts,
} from './instanceCounts';
import { getPaths, toBELabel } from './getPaths';
import { getTriples, processMetaModel } from './processMetaModel';
import {
  getInitialTraversalBuilderViewState,
  getInitialTraversalEditState,
  resetTraversalBuilderState,
} from './initialTraversalState';
import {
  getTraversalAsSavableAttributes,
  getTraversals,
  getTraversalsWithGraphIds,
  getWorkspaceIdsAndComponentAndReferenceTypeNamesFromTraversals,
} from './getTraversals';
import { getGraphEdgeToReferenceMapEntry } from './addNodeAndEdge';
import {
  toInternalPathCollapsingRule,
  toInternalPathCollapsingRules,
  updateEdgesToBeRemovedOnCollapsing,
  updatePathCollapsingRules,
  withResolvedVirtualEdgeStates,
} from './pathCollapsing';
import {
  buildCollapsedGraph,
  removeNodeFromGraph,
} from './rebuildSelectedGraph';
import {
  changeWildcardTraversal,
  expandDefaultTraversal,
  setTraversal,
  updateTripleOptions,
} from './setTraversal';
import {
  setStartTypeAndTraversalStartSetOrCount,
  updateTraversalStartContext,
} from './startContext';
import {
  getSelectedNodeComponentType,
  setTripleSortOrder,
  toggleIsSelectedGraphEdgeAndUpdateTripleOptions,
  toggleIsSelectedGraphNodeAndUpdateTripleOptions,
} from './toggleSelection';
import { toggleTripleOption } from './toggleTripleOption';
import { setSectionFilter } from './updateFilters';
import type { SetTraversalsPayload } from './editTraversalActions';
import { metaModelOperations } from 'architectureModel/metaModelOperations';
import { anyType } from '@ardoq/global-consts';
import { getTriplesFromPathsWithoutHierarchyHops } from './getTriplesFromPathsWithoutHierarchyHops';

const dontShowAgainClickOtherComponentTypeNodesHints = (
  state: TraversalBuilderState
) => {
  const newGraphNodes = new Map(state.graphNodes);
  Array.from(newGraphNodes.values()).forEach(graphNode => {
    graphNode.setMetaData({
      ...graphNode.metaData,
      shouldDisplayClickOtherComponentsHint: false,
    });
  });
  return {
    ...state,
    graphNodes: newGraphNodes,
    shouldIncludeClickOtherComponentTypeNodesHintsRestoredFromUserSettings: false,
  };
};

const processCurrentUserSettings = (
  state: TraversalBuilderState,
  { settings }: APICurrentUser
) => {
  return {
    ...state,
    shouldIncludeClickOtherComponentTypeNodesHintsRestoredFromUserSettings:
      settings[
        PersonalSetting
          .INCLUDE_CLICK_OTHER_COMPONENT_TYPE_NODES_HINTS_IN_VIEWPOINT_BUILDER
      ] ?? true,
  };
};

const setPathMatchingType = (
  state: TraversalBuilderState,
  pathMatching: TraversalPathMatchingType
) => ({
  ...state,
  pathMatching,
});

// Both endpoints of a hop can exist while its triple is gone from the
// metamodel, so this is checked per triple: `extendMetamodelWithTriples` folds
// in absent triples too, and without that `pathsToGraph` truncates the hop.
const hasPathTripleMissingFromMetaModel = (
  { metaModelTriples }: TraversalBuilderState,
  paths: SetTraversalsPayload['paths']
): boolean => {
  if (!paths || paths.length === 0) {
    return false;
  }
  const knownTriples = new Set(
    metaModelTriples.map(metaModelOperations.toNormalizedTripleKey)
  );
  return getTriplesFromPathsWithoutHierarchyHops(paths)
    .filter(
      // The sentinel on both endpoints is the all-wildcard traversal, which
      // `getRawGraphEdge` resolves elsewhere and no triple describes.
      ({ sourceComponentType, targetComponentType }) =>
        sourceComponentType !== anyType || targetComponentType !== anyType
    )
    .some(
      triple =>
        !knownTriples.has(metaModelOperations.toNormalizedTripleKey(triple))
    );
};

/**
 * Reprocesses rather than rebuilding in place: a plain rebuild of
 * `rawGraphNodes` drops the hierarchy node `applyStartSetExpansion` injects.
 * `processMetaModel` reduces with the unwrapped `setTraversal`, so this cannot
 * recurse.
 */
const setTraversalAndReprocessMetaModel = (
  state: TraversalBuilderState,
  payload: SetTraversalsPayload
): TraversalBuilderState =>
  state.metaModel && hasPathTripleMissingFromMetaModel(state, payload.paths)
    ? processMetaModel(
        {
          ...state,
          initialPaths: payload.paths ?? null,
          initialFilters: payload.filters ?? null,
        },
        state.metaModel
      )
    : setTraversal(state, payload);

export const editTraversalOperations = {
  applyStartSetExpansion,
  buildCollapsedGraph,
  changeWildcardTraversal,
  filterOptions,
  getGraphEdgeToReferenceMapEntry,
  getInitialTraversalBuilderViewState,
  getInitialTraversalEditState,
  getPaths,
  getSelectedNodeComponentType,
  getTraversalAsSavableAttributes,
  getTraversals,
  getTraversalsWithGraphIds,
  getTriples,
  getWorkspaceIdsAndComponentAndReferenceTypeNamesFromTraversals,
  pathsToGraph,
  processCurrentUserSettings,
  processMetaModel,
  removeNodeFromGraph,
  resetTraversalBuilderState,
  dontShowAgainClickOtherComponentTypeNodesHints,
  setInstanceCounts,
  updateStartNodeCountForCollapsedHierarchy,
  invalidateInstanceCounts,
  setOptionCountLoadingState,
  setOptionsInstanceCounts,
  setPathMatchingType,
  setSectionFilter,
  setStartTypeAndTraversalStartSetOrCount,
  setTraversal: setTraversalAndReprocessMetaModel,
  setTripleSortOrder,
  toggleIsSelectedGraphEdgeAndUpdateTripleOptions,
  toggleIsSelectedGraphNodeAndUpdateTripleOptions,
  toggleShowOnlyOptionsWithInstanceCounts,
  toggleTripleOption,
  toInternalPathCollapsingRule,
  toInternalPathCollapsingRules,
  withResolvedVirtualEdgeStates,
  tripleOptionToFilteredAndSortedTripleOptions,
  updateTraversalStartContext,
  updateEdgesToBeRemovedOnCollapsing,
  updatePathCollapsingRules,
  toBELabel,
  updateTripleOptions,
  expandDefaultTraversal,
};

export { filterOptions } from './filterOptions';
